import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import { Select } from "antd";
import { ROLL_CALL_API_BASE_URL } from "../../../RollCall/RollCallConfig/RollCallConfig";

const AddKpiMetricDetails = ({ fetchMetricDetails, onClose, isOpen }) => {
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [formData, setFormData] = useState({
    MetricId: null,
    Title: "",
    Description: "",
    BranchId: 0,
    HasText: false,
    HasFlag: false,
    HasValue: false,
    Frequency: "",
  });

  const [metrics, setMetrics] = useState([]);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");

    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    }
  }, []);

  const fetchMetrics = async (kpiGroup) => {
    if (!tokenData || !superIdData || !kpiGroup) return;

    setMetricsLoading(true);
    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/kpi/getkpimetrics?KPIGroup=${kpiGroup}&SuperId=${superIdData}&BranchId=0`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );

      const data = await response.json();

      if (data?.status && Array.isArray(data.ResultData)) {
        const options = data.ResultData.map((item) => ({
          label: item.Title,
          value: item.id,
        }));
        setMetrics(options);
      } else {
        setMetrics([]);
      }
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics([]);
      Swal.fire("Error", "Failed to load metrics", "error");
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleFrequencyChange = (value) => {
    let kpiGroup = null;

    if (value === "daily") kpiGroup = 1;
    else if (value === "weekly") kpiGroup = 2;
    else if (value === "monthly") kpiGroup = 3;

    setFormData((prev) => ({
      ...prev,
      Frequency: value,
      MetricId: null,
    }));

    if (kpiGroup) {
      fetchMetrics(kpiGroup);
    } else {
      setMetrics([]);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" || type === "radio" ? checked : value,
    }));
  };

  const handleInputTypeChange = (type) => {
    setFormData({
      ...formData,
      HasText: type === "text",
      HasFlag: type === "flag",
      HasValue: type === "value",
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.MetricId || !formData.Title || !formData.Description) {
      Swal.fire("Warning", "Please fill all required fields", "warning");
      setLoading(false);
      return;
    }

    const payload = {
      MetricId: Number(formData.MetricId),
      Title: formData.Title,
      Description: formData.Description,
      SuperId: Number(superIdData),
      BranchId: Number(formData.BranchId),
      HasText: formData.HasText ? 1 : 0,
      HasFlag: formData.HasFlag ? 1 : 0,
      HasValue: formData.HasValue ? 1 : 0,
      CreatedBy: 1,
    };

    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/kpi/addKpiMetricDetails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();

      if (result?.status) {
        Swal.fire({
          title: "Success",
          text: "Metric Details Added Successfully",
          icon: "success",
        }).then(() => {
          fetchMetricDetails();
          handleCancel();
          onClose();
        });
      } else {
        Swal.fire("Error", result.message || "Failed to add metric", "error");
      }
    } catch (error) {
      console.error("Error adding metric:", error);
      Swal.fire("Error", "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      MetricId: null,
      Title: "",
      Description: "",
      BranchId: 0,
      HasText: false,
      HasFlag: false,
      HasValue: false,
      Frequency: "",
    });
    setMetrics([]);
  };

  const handleClose = () => {
    handleCancel();
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div>
          <style>{`
            @media (max-width: 576px) { #offcanvasRightAddKpiMetricDetails { width: 90% !important; } }
            @media (min-width: 577px) and (max-width: 992px) { #offcanvasRightAddKpiMetricDetails { width: 55% !important; } }
            @media (min-width: 993px) and (max-width: 1440px) { #offcanvasRightAddKpiMetricDetails { width: 35% !important; } }
            @media (min-width: 1441px) { #offcanvasRightAddKpiMetricDetails { width: 28% !important; } }
          `}</style>

          <div className="offcanvas-backdrop fade show" onClick={handleClose} />

          <div
            className="offcanvas offcanvas-end show"
            id="offcanvasRightAddKpiMetricDetails"
            style={{ visibility: "visible" }}
          >
            <div className="offcanvas-header">
              <h4>Add Metric Details</h4>
              <button
                type="button"
                className="btn-close text-reset"
                onClick={handleClose}
                aria-label="Close"
              />
            </div>
            <hr className="m-0 p-0" />

            <div className="offcanvas-body">
              <form onSubmit={handleSave}>
                <div className="row">
                  {/* Frequency */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <span className="text-danger">*</span> Frequency
                    </label>
                    <Select
                      className="w-100"
                      placeholder="Select Frequency"
                      value={formData.Frequency || undefined}
                      onChange={handleFrequencyChange}
                      options={[
                        { label: "Daily", value: "daily" },
                        { label: "Weekly", value: "weekly" },
                        { label: "Monthly", value: "monthly" },
                      ]}
                    />
                  </div>

                  {/* Metric Dropdown (Dynamic) */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      <span className="text-danger">*</span> Metric
                    </label>
                    <Select
                      className="w-100"
                      placeholder="Select Metric"
                      value={formData.MetricId || undefined}
                      onChange={(value) =>
                        setFormData((prev) => ({ ...prev, MetricId: value }))
                      }
                      options={metrics}
                      loading={metricsLoading}
                      disabled={!formData.Frequency}
                    />
                  </div>

                  {/* Title */}
                  <div className="col-12 mb-3">
                    <label className="form-label">
                      <span className="text-danger">*</span> Title
                    </label>
                    <input
                      type="text"
                      name="Title"
                      className="form-control"
                      placeholder="Enter title"
                      value={formData.Title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Description */}
                  <div className="col-12 mb-3">
                    <label className="form-label">
                      <span className="text-danger">*</span> Description
                    </label>
                    <textarea
                      name="Description"
                      className="form-control"
                      placeholder="Enter description"
                      rows={3}
                      value={formData.Description}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Input Type */}
                  <div className="col-12 mb-4">
                    <label className="form-label d-block">
                      <span className="text-danger">*</span> Input Type
                    </label>
                    <div className="d-flex gap-4 mt-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="inputType"
                          checked={formData.HasText}
                          onChange={() => handleInputTypeChange("text")}
                        />
                        <label className="form-check-label">Text</label>
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="inputType"
                          checked={formData.HasFlag}
                          onChange={() => handleInputTypeChange("flag")}
                        />
                        <label className="form-check-label">
                          Checkbox (Yes/No)
                        </label>
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="inputType"
                          checked={formData.HasValue}
                          onChange={() => handleInputTypeChange("value")}
                        />
                        <label className="form-check-label">Number</label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="row d-flex justify-content-end mt-4">
                  <div className="col-3 d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                  </div>
                  <div className="col-3 d-flex justify-content-end">
                    <button
                      type="submit"
                      className="btn btn-success"
                      disabled={loading || !formData.MetricId}
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

AddKpiMetricDetails.propTypes = {
  fetchMetricDetails: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default AddKpiMetricDetails;
